import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { productionApi } from '~/api/production';

import { getInvalidProductCount, processInvalidProducts, getValidIds } from '../../utils/validateProducts';
import { SLICE_REGISTRY } from '../../utils/constants';
import { parseCSVWithRanks, orderItemsByRank } from '../utils/csvUtils';
import scrollToSliceCard from '../utils/scrollToSliceCard';
import { generateSlug } from '../utils/slugUtils';
import validateSlices from '../utils/validateSlices';

import { useDataLoader } from './DataLoaderProvider';

const FormContextState = createContext({});
const FormContextControls = createContext({});

/**
 * Provider component that manages form state and actions for the Storefront Configuration Editor.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components that will have access to form state and actions
 * @returns {JSX.Element} Provider component wrapping children with form state and actions contexts
 */
export function FormStateProvider({ children }) {
    const { config, isCreateMode, setConfig } = useDataLoader();

    const [sellableBundles, setSellableBundles] = useState();
    const [sellableItems, setSellableItems] = useState();
    const [loadingServerData, setLoadingServerData] = useState(false);

    const [formData, setFormData] = useState(null);
    const [formInitialized, setFormInitialized] = useState(false);

    const [highlightedSliceId, setHighlightedSliceId] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);

    const [duplicateSlicesDialog, setDuplicateSlicesDialog] = useState({ open: false, sourceSlice: null });

    const newSliceIdRef = useRef(null);
    const sliceCardRefs = useRef({});

    // Add ref to store current formData without creating dependencies
    const formDataRef = useRef(formData);

    const history = useHistory();
    const searchParams = new URLSearchParams(useLocation().search);
    const packGroupID = searchParams.get('packGroupID');

    const numSlices = formData?.slices?.length || 0;

    const invalidProductCount =
        sellableBundles && sellableItems
            ? getInvalidProductCount(formData, [...(sellableBundles ?? []), ...(sellableItems ?? [])])
            : -1;

    // Keep formDataRef in sync with formData changes
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        if (config && (!formInitialized || config?.id !== formData?.id)) {
            setFormData({
                ...config,
                ...(packGroupID ? { packGroupID: Number(packGroupID) } : {}),
            });
            setFormInitialized(true);
        }
    }, [config, packGroupID, formInitialized]);

    useEffect(() => {
        const packGroupID = formData?.packGroupID;
        if (!packGroupID) return;

        const carouselTypes = ['PRODUCT_CAROUSEL_1', 'PRODUCT_CAROUSEL_2', 'PRODUCT_CAROUSEL_3', 'PRODUCT_SPOTLIGHT'];

        const fetchAllData = async () => {
            setLoadingServerData(true);
            const loadingToastId = toast.loading('Loading storefront data...');

            try {
                // 1. Extract carousel product IDs by slice
                const productIDs = formData.slices.reduce((acc, slice) => {
                    if (!carouselTypes.includes(slice.displayType)) return acc;

                    const SliceComponent = SLICE_REGISTRY[slice.displayType];
                    const productListKey = SliceComponent?.productListKey;
                    const productList = slice[productListKey];

                    if (!productListKey || !Array.isArray(productList)) return acc;

                    return {
                        ...acc,
                        [slice.id]: productList.map((product) => product.id),
                    };
                }, {});

                // 2. Fetch sellable bundles, items, and converted items
                const [bundlesRes, itemsRes, convertedItemsRes] = await Promise.all([
                    productionApi.getSellableBundles(packGroupID),
                    productionApi.getSellableItems(packGroupID),
                    productionApi.getConvertedSellableItems(packGroupID, productIDs),
                ]);

                setSellableBundles(bundlesRes.data.sellableBundles);
                setSellableItems(itemsRes.data.sellableItems);

                // 3. Update slices with converted items
                const updatedSlices = formData.slices.map((slice) => {
                    if (!carouselTypes.includes(slice.displayType)) return slice;

                    const SliceComponent = SLICE_REGISTRY[slice.displayType];
                    const productListKey = SliceComponent?.productListKey;
                    const updatedProducts = convertedItemsRes?.data?.carousels?.[String(slice.id)];

                    if (!productListKey || !updatedProducts) return slice;

                    return {
                        ...slice,
                        [productListKey]: updatedProducts,
                    };
                });

                // 4. Update form data
                setFormData((prev) => ({
                    ...prev,
                    slices: updatedSlices,
                }));
            } catch (err) {
                console.error('Error loading storefront data:', err);
            } finally {
                setLoadingServerData(false);
                toast.dismiss(loadingToastId);
            }
        };

        fetchAllData();
    }, [formData?.packGroupID]);

    /**
     * Config Handlers
     */

    const deleteConfig = useCallback(
        async (configId) => {
            if (isCreateMode) {
                history.replace('/production/storefront-configurations');
                toast.success('Configuration deleted successfully!');
                return;
            }

            try {
                setLoadingServerData(true);
                await productionApi.deleteStorefrontConfiguration(configId);
                toast.success('Configuration deleted successfully!');
                history.replace('/production/storefront-configurations');
            } catch (error) {
                console.error('Error deleting configuration:', error);
            } finally {
                setLoadingServerData(false);
            }
        },
        [isCreateMode],
    );

    const duplicateConfig = useCallback(async (formData, name, packGroupIDs) => {
        try {
            setLoadingServerData(true);
            await Promise.all(
                packGroupIDs.map(async (packGroupID) => {
                    // Convert items to current pack group (this will *remove* invalid items)
                    const productMap = {};
                    formData.slices.forEach((slice) => {
                        if (slice.carouselID) {
                            productMap[slice.carouselID] = slice.items.map(
                                (item) => item.externalSku || item.external_sku,
                            );
                        }
                    });

                    const sellableItemsRes = await productionApi.getSellableItemsByExternalSku(productMap, packGroupID);
                    const convertedItemsByCarouselID = sellableItemsRes.data.carousels;

                    const duplicateData = {
                        ...formData,
                        id: null,
                        name,
                        packGroupID,
                        published: false,
                        slices: formData.slices.map((slice) => {
                            const cleanedSlice = {
                                ...slice,
                                id: null,
                                ...(slice.carouselID && { carouselID: null }),
                                ...(slice.carouselID && { items: convertedItemsByCarouselID[slice.carouselID] }),
                            };

                            if (slice.name) {
                                cleanedSlice.slug = generateSlug(slice.name);
                            }

                            return cleanedSlice;
                        }),
                    };

                    await productionApi.saveStorefrontConfiguration(duplicateData);
                }),
            );

            toast.success('Configuration duplicated successfully!');
        } catch (error) {
            console.error('Error duplicating configuration:', error);
            toast.error('Failed to duplicate configuration');
        } finally {
            setLoadingServerData(false);
        }
    }, []);

    const publishConfig = useCallback(
        async (e, data) => {
            const errors = validateSlices(data.slices);

            if (errors.length > 0 && !data.published) {
                setValidationErrors(errors);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                toast.error('Validation errors found.', {
                    description: 'Please fix them before saving.',
                });
                return;
            }

            setValidationErrors([]);

            setFormData({ ...data, published: !data.published });

            try {
                setLoadingServerData(true);
                await saveConfig(e, { ...data, published: !data.published });
            } catch (error) {
                console.error(`Error updating publication status:`, error);
            } finally {
                setLoadingServerData(false);
            }
        },
        [isCreateMode],
    );

    const saveConfig = useCallback(
        async (e, data) => {
            e.preventDefault();

            if (data.published) {
                const errors = validateSlices(data.slices);

                if (errors.length > 0) {
                    setValidationErrors(errors);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    toast.error('Validation errors found.', {
                        description: 'Please fix them before saving.',
                    });
                    return;
                }

                setValidationErrors([]);
            }

            if (isCreateMode) {
                try {
                    setLoadingServerData(true);
                    const response = await productionApi.saveStorefrontConfiguration(data);
                    toast.success('Configuration created successfully!');
                    history.replace(`/production/storefront-configurations/${response.data.id}`);
                } catch (error) {
                    console.error(`Error creating configuration:`, error);
                } finally {
                    setLoadingServerData(false);
                }
            } else {
                try {
                    setLoadingServerData(true);
                    await productionApi.saveStorefrontConfiguration(data, true);
                    const response = await productionApi.getStorefrontConfigurationByID(data.id);
                    const updatedConfig = response.data.storefrontConfiguration;
                    setFormData(updatedConfig);
                    setConfig(updatedConfig);
                    toast.success('Changes saved successfully!');
                } catch (error) {
                    toast.error('Failed to save changes.', {
                        description: 'Please try again.',
                    });
                    console.error(`Error updating configuration:`, error);
                } finally {
                    setLoadingServerData(false);
                }
            }
        },
        [isCreateMode, setConfig],
    );

    const updateConfig = useCallback(
        (updates) => {
            setFormData((prev) => ({ ...prev, ...updates }));
        },
        [isCreateMode],
    );

    /**
     * Slice Handlers
     */

    const addSlice = useCallback((displayType, index) => {
        setFormData((prev) => {
            const newSlice = buildSlice(displayType);

            newSliceIdRef.current = newSlice.id;

            const updatedSlices = [...prev.slices];
            updatedSlices.splice(index, 0, newSlice);
            return { ...prev, slices: updatedSlices };
        });

        highlightSlice(newSliceIdRef.current);
        newSliceIdRef.current = null;
    }, []);

    const buildSlice = (displayType, baseSlice = {}) => {
        const tempId = `temp-${crypto.randomUUID()}`;
        const defaultSliceValues = SLICE_REGISTRY[displayType]?.defaultSlice ?? {};

        return {
            ...defaultSliceValues,
            ...baseSlice,
            id: tempId,
            displayType,
        };
    };

    const clearHighlightedSlice = useCallback(() => {
        setHighlightedSliceId(null);
    }, []);

    const deleteSlice = useCallback((index) => {
        setFormData((prev) => {
            const slices = prev.slices.filter((_, i) => i !== index);
            return { ...prev, slices };
        }, []);
    }, []);

    const duplicateSlice = useCallback((index) => {
        setFormData((prev) => {
            const sliceToDuplicate = prev.slices[index];
            const duplicatedSlice = buildSlice(sliceToDuplicate.displayType, sliceToDuplicate);
            const newSlice = {
                ...duplicatedSlice,
                ...(duplicatedSlice.carouselID && { carouselID: null }),
            };

            newSliceIdRef.current = newSlice.id;

            const slices = [...prev.slices];
            slices.splice(index + 1, 0, newSlice);
            return { ...prev, slices };
        });

        highlightSlice(newSliceIdRef.current);
        newSliceIdRef.current = null;
    }, []);

    const highlightSlice = useCallback((sliceId, persistHighlight = false) => {
        setHighlightedSliceId((prev) => (prev === sliceId && !persistHighlight ? null : sliceId));
        scrollToSliceCard(sliceCardRefs, sliceId);
    }, []);

    const moveSlice = useCallback((oldIndex, newIndex) => {
        setFormData((prev) => {
            const slices = [...prev.slices];
            const [movedSlice] = slices.splice(oldIndex, 1);

            newSliceIdRef.current = movedSlice.id;

            slices.splice(newIndex, 0, movedSlice);
            return { ...prev, slices };
        });

        highlightSlice(newSliceIdRef.current, true);
        newSliceIdRef.current = null;
    }, []);

    const updateSlice = useCallback((index, updates) => {
        setFormData((prev) => {
            const slices = [...prev.slices];
            slices[index] = { ...slices[index], ...updates };
            return { ...prev, slices };
        });
    }, []);

    /**
     * Misc. handlers
     */

    const downloadCarouselCSV = useCallback((formData) => {
        const rows = [];
        const headers = ['Carousel ID', 'Carousel Name', 'Carousel Type'];

        rows.push(headers);

        formData.slices.forEach((slice) => {
            if (slice.carouselID) {
                const row = [slice.carouselID, slice.name, slice.displayType];
                rows.push(row);
            }
        });

        const csvContent = rows.map((row) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });

        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = `${formData.name} Carousels.csv`;
        downloadLink.click();
        URL.revokeObjectURL(downloadLink.href);
    }, []);

    const uploadProductCSV = useCallback(async (file, formData) => {
        try {
            setLoadingServerData(true);

            const carouselTypes = [
                'PRODUCT_CAROUSEL_1',
                'PRODUCT_CAROUSEL_2',
                'PRODUCT_CAROUSEL_3',
                'PRODUCT_SPOTLIGHT',
            ];

            const packGroupID = formData?.packGroupID;
            if (!packGroupID) return;

            const fileText = await file.text();
            const parsedRows = parseCSVWithRanks(fileText);

            const carouselProducts = {};
            parsedRows.forEach(({ carouselID, sku, rank }) => {
                if (carouselID && sku) {
                    if (!carouselProducts[carouselID]) {
                        carouselProducts[carouselID] = [];
                    }
                    carouselProducts[carouselID].push({ sku, rank });
                }
            });

            if (Object.keys(carouselProducts).length === 0) {
                toast.error('No valid product IDs found in the uploaded CSV file.');
                return;
            }

            const skusToLookup = {};
            for (const carouselID in carouselProducts) {
                skusToLookup[carouselID] = carouselProducts[carouselID].map((p) => p.sku);
            }

            const sellableItemsRes = await productionApi.getSellableItemsByExternalSku(skusToLookup, packGroupID);

            const updatedSlices = formData.slices.map((slice) => {
                if (!carouselTypes.includes(slice.displayType)) return slice;

                const SliceComponent = SLICE_REGISTRY[slice.displayType];
                const productListKey = SliceComponent?.productListKey;
                const rawSkuEntries = carouselProducts[slice.carouselID];
                const resolvedItems = sellableItemsRes?.data?.carousels?.[slice.carouselID];

                if (!productListKey || !rawSkuEntries || !resolvedItems) return slice;

                const itemMap = Object.fromEntries(resolvedItems.map((item) => [item.externalSku, item]));
                const finalOrdered = orderItemsByRank(rawSkuEntries, itemMap);

                return {
                    ...slice,
                    [productListKey]: finalOrdered,
                };
            });

            setFormData((prev) => ({ ...prev, slices: updatedSlices }));
            toast.success('Products uploaded successfully!');
        } catch (error) {
            console.error('Error uploading product CSV:', error);
            toast.error(error.message || 'Failed to upload products.');
        } finally {
            setLoadingServerData(false);
        }
    }, []);

    const uploadProductCSVForSlice = useCallback(async (file, slice, index) => {
        try {
            setLoadingServerData(true);

            const fileText = await file.text();
            const parsedRows = parseCSVWithRanks(fileText);

            const filtered = parsedRows.filter((r) => r.carouselID === slice.carouselID.toString() && r.sku);
            if (filtered.length === 0) {
                toast.error(`No rows found in CSV for carousel ID "${slice.carouselID}".`);
                return;
            }

            const skus = filtered.map((r) => r.sku);
            const sellableItemsRes = await productionApi.getSellableItemsByExternalSku(
                { [slice.carouselID]: skus },
                formDataRef.current?.packGroupID,
            );

            const resolvedItems = sellableItemsRes?.data?.carousels?.[slice.carouselID];
            if (!resolvedItems) {
                toast.error('No matching sellable items found.');
                return;
            }

            const itemMap = Object.fromEntries(resolvedItems.map((item) => [item.externalSku, item]));
            const finalOrdered = orderItemsByRank(filtered, itemMap);

            updateSlice(index, {
                ...slice,
                items: finalOrdered,
            });

            toast.success('Products uploaded successfully!');
        } catch (error) {
            console.error('Error uploading product CSV:', error);
            toast.error(error.message || 'Failed to upload products.');
        } finally {
            setLoadingServerData(false);
        }
    }, []);

    const removeInvalidProducts = useCallback(() => {
        setFormData((prev) => {
            const validIds = getValidIds([...(sellableBundles ?? []), ...(sellableItems ?? [])]);
            const updatedSlices = prev.slices.map((slice) => processInvalidProducts(slice, validIds).slice);
            return { ...prev, slices: updatedSlices };
        });
    }, [sellableBundles, sellableItems]);

    /**
     * Misc. Handlers
     */

    const toggleDuplicateSlicesDialog = useCallback((sourceSlice) => {
        setDuplicateSlicesDialog((prev) => ({
            open: !prev.open,
            sourceSlice: prev.open ? null : sourceSlice,
        }));
    }, []);

    /**
     * Memoized form data that will cause the entire editor to rerender when changed.
     * This object should only contain values that NEED to trigger rerenders of components
     * that consume this data.
     */
    const formContextState = useMemo(
        () => ({
            formData,
        }),
        [formData],
    );

    /**
     * Memoized form actions and stable data that should NOT trigger unnecessary rerenders.
     * This object contains methods and values that are either:
     * 1. Callback functions that are already memoized with useCallback
     * 2. Stable values that change infrequently
     *
     * WARNING: Adding unstable/frequently changing values here will *significantly* degrade performance
     * by causing unnecessary rerenders of all components consuming the FormContextControls.
     */
    const formContextControls = useMemo(
        () => ({
            addSlice,
            clearHighlightedSlice,
            deleteConfig,
            deleteSlice,
            downloadCarouselCSV,
            duplicateConfig,
            duplicateSlice,
            duplicateSlicesDialog,
            highlightSlice,
            highlightedSliceId,
            invalidProductCount,
            loadingServerData,
            moveSlice,
            numSlices,
            publishConfig,
            removeInvalidProducts,
            saveConfig,
            sellableBundles,
            sellableItems,
            sliceCardRefs,
            toggleDuplicateSlicesDialog,
            updateConfig,
            updateSlice,
            uploadProductCSV,
            uploadProductCSVForSlice,
            validationErrors,
        }),
        [
            addSlice,
            clearHighlightedSlice,
            deleteConfig,
            deleteSlice,
            downloadCarouselCSV,
            duplicateConfig,
            duplicateSlice,
            duplicateSlicesDialog,
            highlightSlice,
            highlightedSliceId,
            invalidProductCount,
            loadingServerData,
            moveSlice,
            numSlices,
            publishConfig,
            removeInvalidProducts,
            saveConfig,
            sellableBundles,
            sellableItems,
            sliceCardRefs,
            toggleDuplicateSlicesDialog,
            updateConfig,
            updateSlice,
            uploadProductCSV,
            uploadProductCSVForSlice,
            validationErrors,
        ],
    );

    return (
        <FormContextState.Provider value={formContextState}>
            <FormContextControls.Provider value={formContextControls}>{children}</FormContextControls.Provider>
        </FormContextState.Provider>
    );
}

export function useFormState() {
    return useContext(FormContextState);
}

export function useFormControls() {
    return useContext(FormContextControls);
}
